/**
 * MARKETING_NAV_LINKS — canonical nav set for marketing surfaces.
 *
 * Shared by HomeHeader and PublicHeader so the link set never drifts between
 * the homepage and inner marketing pages.
 */

import { useRouter } from '@tanstack/react-router';
import type { CSSProperties } from 'react';
import { useEffect } from 'react';

import { useLaunchState } from './auth-context';

// Both `/our-ethos` and `/ripples` stay in this union even though
// neither is a top-nav tab any more (see MARKETING_NAV_LINKS): it is the
// vocabulary `isMarketingNavActive` and `MarketingNavHref` are written against,
// and the two pages are still live, still in the sitemap, and still linked from
// the footer's trust column (SiteFooter keeps its own wider `FooterTo` union).
type MarketingNavTo =
  | '/browse'
  | '/how-it-works'
  | '/our-ethos'
  | '/founding-venues'
  | '/ripples'
  | '/our-story';

/** Where a marketing nav/footer link actually points once launch state is applied. */
export type MarketingNavHref = MarketingNavTo | '/earlyaccess';

export interface MarketingNavLink {
  readonly to: MarketingNavTo;
  readonly label: string;
}

/** A nav link with its launch-aware destination and current-page state resolved. */
export interface ResolvedMarketingNavLink {
  readonly to: MarketingNavHref;
  readonly label: string;
  readonly active: boolean;
}

// FAQ moved off the top nav (it lives in the footer's trust links) and "Our
// Story" added, per feedback 2026-07-03. Titles set to Richie's wording +
// Title Case (2026-07-06): Browse → Browse Venues, Notes → Ripples. Sign In /
// Sign Up are the header's auth CTAs, not nav links.
//
// Ripples (/ripples) came off the top nav 2026-08-18 (feedback
// HpoZ1F0of6m5XKnkdZ99t). The PAGE is untouched and still live: the footer's
// trust column carries it, the "Read on" cards signpost it, it is in the
// sitemap, and it is the deep-link inside already-sent founder-letter emails.
// Removing the route is a different decision entirely.
//
// Our Ethos went with it in that same pass and came BACK the same day at
// Richie's request, in this position — between Founding Venues and Our Story.
// Keep it there: Terms section 2 links to that page as the place the promise
// lives, so it needs a route a reader can actually find.
export const MARKETING_NAV_LINKS: ReadonlyArray<MarketingNavLink> = [
  { to: '/browse', label: 'Browse Venues' },
  { to: '/how-it-works', label: 'How It Works' },
  { to: '/founding-venues', label: 'Founding Venues' },
  { to: '/our-ethos', label: 'Our Ethos' },
  { to: '/our-story', label: 'Our Story' },
];

/**
 * Where "Browse Venues" should send a visitor right now.
 *
 * `/browse` is only a real destination once the `public_browse` flag is `all`.
 * While it is `founding_cohort` (the current pre-launch state) the venue search
 * endpoint 403s every anonymous and non-cohort caller, so the page renders a
 * "Couldn't load venues right now" error — a dead end reached from a link the
 * marketing header and footer both advertise publicly (feedback
 * ZxI3VUjjCqKnNWM_yl2eh). Pre-launch the interest goes to the waitlist capture
 * instead, which is the same destination every other pre-launch CTA uses.
 *
 * This is deliberately keyed on `public_browse` rather than a hardcoded switch
 * so it reverts itself the moment browse is actually opened — no code change,
 * no second round of this bug.
 *
 * While the launch query is in flight we assume the pre-launch case, matching
 * `JoinLink`: that is the current reality, and the waitlist page is a safe
 * landing either way.
 */
export function useBrowseDestination(): MarketingNavHref {
  const { data } = useLaunchState();
  return data?.features['public_browse'] === 'all' ? '/browse' : '/earlyaccess';
}

/**
 * True when `pathname` is the page `to` names — exact match, or a child route
 * beneath it (so a child page such as `/browse/artists` still marks its parent
 * tab).
 *
 * Matched against the CANONICAL route, never the launch-substituted one: while
 * "Browse Venues" points at `/earlyaccess`, standing on the waitlist page should
 * not light up a tab labelled Browse Venues.
 */
export function isMarketingNavActive(pathname: string, to: MarketingNavTo): boolean {
  const path = pathname.toLowerCase().replace(/\/+$/, '') || '/';
  return path === to || path.startsWith(`${to}/`);
}

/**
 * Shared styling for a marketing nav anchor.
 *
 * The current page is marked three ways, because one alone is not enough:
 * brand colour and a heavier weight (visible at a glance), a 2px rule beneath
 * the label on the desktop bar (the "which tab am I on" affordance Richie
 * asked for — feedback W_-eYTDO3mNqjN_cnNOUo), and `aria-current="page"` so
 * screen readers get the same information colour alone would withhold.
 *
 * `variant='drawer'` drops the underline: in the stacked mobile drawer a rule
 * under one item reads as a divider between sections rather than a highlight.
 *
 * Colour is per-scheme rather than a flat `brand-6`: Clay #d2602e measures
 * ~3.7:1 on the light Paper ground and ~3.9:1 on the dark Ink ground, and nav
 * labels are 14px, so plain brand-6 misses AA's 4.5:1 in BOTH schemes. brand-7
 * (~5.7:1 on Paper) and brand-4 (~6.7:1 on Ink) clear it while still reading as
 * the same Clay accent.
 */
const ACTIVE_NAV_COLOR =
  'light-dark(var(--mantine-color-brand-7), var(--mantine-color-brand-4))';

export function marketingNavAnchorStyle(
  active: boolean,
  variant: 'bar' | 'drawer' = 'bar',
): CSSProperties {
  if (!active) return { color: 'var(--mantine-color-text)', fontWeight: 500 };
  return {
    color: ACTIVE_NAV_COLOR,
    fontWeight: 700,
    ...(variant === 'bar'
      ? {
          borderBottom: `2px solid ${ACTIVE_NAV_COLOR}`,
          paddingBottom: 2,
        }
      : {}),
  };
}

/**
 * The nav set with launch-aware destinations and current-page state applied.
 * Callers pass the current pathname (from `useRouterState`) so the hook stays
 * usable from both headers without either re-deriving the rules.
 */
export function useMarketingNavLinks(pathname: string): ReadonlyArray<ResolvedMarketingNavLink> {
  const browseTo = useBrowseDestination();
  const router = useRouter();
  const links = MARKETING_NAV_LINKS.map((l) => ({
    to: l.to === '/browse' ? browseTo : l.to,
    label: l.label,
    active: isMarketingNavActive(pathname, l.to),
  }));

  // Eagerly preload every persistent-nav destination's route chunk on mount,
  // not just on hover/intent. `defaultPreload: 'intent'` (main.tsx) only fires
  // on a dwelled hover, which never happens on a tap — so a mobile visitor (or
  // a fast desktop click with no hover) hit the network for the destination's
  // JS chunk on click, and TanStack Router keeps rendering the CURRENT route
  // while that fetch is in flight. The visible symptom is the previous page's
  // content staying on screen for a beat before flicking to the new one. These
  // are small marketing chunks always reachable from the header, so preloading
  // all of them unconditionally is cheap and makes every header-driven nav
  // transition instant.
  useEffect(() => {
    for (const l of links) {
      void router.preloadRoute({ to: l.to });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-run only when the resolved destination set changes, not on every pathname/active-state re-render
  }, [router, browseTo]);

  return links;
}
